import {MmStructureAddressesComponent} from '../mm-structure-addresses/mm-structure-addresses.component';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Structure} from '@docma-mm-core/structure/structure';
import {CoEvent, CoEventService} from '@mydocma/co';
import {TreeNode, TreeOptions, TreeService} from '@mydocma/component-library/src/lib/tree';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {PlatformModeStateService} from '../../../mm-offline/services/platform-mode-state.service';
import {ConnectivityService} from '@mydocma/core';
import {AppContainerService} from '@mydocma/app-container';
import {distinctUntilChanged, filter} from 'rxjs/operators';
import {Logger} from '@mydocma/common/src/lib/logger';
import {MmProjectSettingsService} from '../../../mm-project-config/configs/project-settings/mm-project-settings.service';
import {MmStructureSyncService, StructureSyncPatch} from '../mm-structure-sync.service';
import {StructureAddressesService} from '../mm-structure-addresses.service';
import {StructureAddress} from '@docma-mm-core/address/structure-address';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {MmStructureAddressesDialogComponent} from '../mm-structure-addresses-dialog/mm-structure-addresses-dialog.component';
import {MmTranslateService} from '../../../mm-translate.service';
import {MmStructureFilterService, StructureFilterDisplayState} from '../mm-structure-filter.service';
import {DeviceService} from '@mydocma/core/src/lib/device';

interface StructureNode {
  expandable: boolean;
  name: string;
  level: number;
  structure: Structure;
}

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-structure-tree',
  templateUrl: './mm-structure-tree.component.html',
  styleUrls: ['./mm-structure-tree.component.scss'],
  providers: [MmStructureFilterService]
})
export class MmStructureTreeComponent implements OnInit, OnDestroy, OnChanges {

  private _structures: Structure[] = [];

  @ViewChild('addressesComponent') private addressesComponent?: MmStructureAddressesComponent;

  @Input() set showIdColumns(value: boolean) {
    this.structureFilterService._showIdColumns = value;
  }

  @Input()
  set structures(value: Structure[]) {
    this._structures = value || [];
  }

  get structures(): Structure[] {
    return this._structures;
  }

  @Input() selected: Structure;
  @Output() structureClick = new EventEmitter<Structure>();

  treeOptions: TreeOptions;
  treeService: TreeService;
  private resizeObserver: ResizeObserver;
  private maxVisibleLevel: number | null = null;

  isAddressInfoOpen = false;
  isExpanded = false;
  hasAddresses = false;
  AddressInfoNodeElement: HTMLElement;

  currentAddresses: StructureAddress[];
  currentStructure: Structure;

  constructor(private readonly coEventService: CoEventService,
              private platformModeStateService: PlatformModeStateService,
              private connectivityService: ConnectivityService,
              public readonly appContainerService: AppContainerService,
              private readonly hostRef: ElementRef,
              private readonly projectSettingsService: MmProjectSettingsService,
              private readonly mmStructureSyncService: MmStructureSyncService,
              private renderer2: Renderer2,
              private readonly logger: Logger,
              public readonly structureAddressesService: StructureAddressesService,
              public dialog: MatDialog,
              private readonly translateService: MmTranslateService,
              public readonly structureFilterService: MmStructureFilterService,
              public readonly deviceService: DeviceService) {

    // if (!this.showIdColumns) {
    //   this.structureFilterService.ADDRESS_FIELD_ORDER =
    //     this.structureFilterService.ADDRESS_FIELD_ORDER.filter(
    //       field => field !== 'Id' && field !== 'Uid'
    //     );
    // }

    this.translateService.onLangChange
      .subscribe(() => {

        for (
          const state
          of this.structureFilterService.filterDisplayStates.values()
          ) {
          this.structureFilterService.innerHtmlByStructureId.set(
            state.structure.Id,
            this.structureFilterService.renderFilterInnerHtml(state)
          );
        }

        // this.cdr.markForCheck();
      });

    this.structureAddressesService.hasAddresses$
      .pipe(untilDestroyed(this))
      .subscribe(hasAddresses => { this.hasAddresses = hasAddresses; });

    this.mmStructureSyncService.patch$
      .pipe(untilDestroyed(this))
      .subscribe(patch => this.applyStructurePatch(patch));

    this.treeOptions = {
      suppressChildrenCount: true,
      collapseAllTopLevel: 1,
      toolbar: {
        buttons: {
          search: {
            tooltip: 'MM.structure.treeview.search.tooltip'
          }
        }
      },
      getNodeId: (data: Structure) => data.Id.toString(),
      // it give s chidren for node , crreate fuctioin
      getNodeChildren: (node: TreeNode) => {
        const children = node.data.Children;
        if (!this.maxVisibleLevel) {
          return children;
        }
        if (node.level >= this.maxVisibleLevel) {
          return null;
        }
        return children;

      },
      getNodeInfo: (node: TreeNode) => {
        return {
          text: [node.data.Name1, node.data.Name2].filter(Boolean).join(' - '),
          expanded: node.data.Level < this.structures[0].Level + 1,
          selectable: node.data.selectable,
          hidden: node.data.hidden
        };
      },
      isNodeInFilter: (node: TreeNode, filters: string[]) => {
        const structure =
          node.data as Structure & {
            innerHtml?: string
          };

        if (!structure) {
          return false;
        }

        const filterTerms =
          this.structureFilterService.normalizeFilterTerms(filters);

        /*
         * No filter.
         */
        if (filterTerms.length === 0) {

          this.structureFilterService.filterDisplayStates.delete(
            structure.Id
          );

          this.structureFilterService.clearInnerHtml(
            structure
          );

          return true;
        }

        const nameValues = [
          structure.Name1 ?? '',
          structure.Name2 ?? ''
        ];

        const structureAddresses =
          this.structureAddressesService
            .StructureAddresses
            .get(structure.Id) ?? [];

        const addressFields =
          this.structureFilterService.getSearchableAddressFields(
            structureAddresses
          );

        /*
         * Every filter must match somewhere.
         */
        const matchesAllFilters =
          filterTerms.every(filter => {

            const matchesStructure =
              nameValues.some(value =>
                this.structureFilterService.containsIgnoreCase(
                  value,
                  filter
                )
              );

            if (matchesStructure) {
              return true;
            }

            return addressFields.some(field =>
              this.structureFilterService.containsIgnoreCase(
                field.value,
                filter
              )
            );
          });

        if (!matchesAllFilters) {

          this.structureFilterService.filterDisplayStates.delete(
            structure.Id
          );

          this.structureFilterService.clearInnerHtml(
            structure
          );

          return false;
        }

        /*
         * Uses your priority logic:
         *
         * DisplayName
         * Name1
         * FirstName
         * ...
         *
         * and selects the first matching field
         * per StructureAddress.
         */
        const matchedFieldGroups =
          this.structureFilterService.getMatchedAddressFieldGroups(
            addressFields,
            filterTerms
          );

        /*
         * Store LANGUAGE-INDEPENDENT result.
         */
        const displayState:
          StructureFilterDisplayState = {

          structure,
          filterTerms,
          matchedFieldGroups
        };

        this.structureFilterService.filterDisplayStates.set(
          structure.Id,
          displayState
        );

        /*
         * Rendering is separate from matching.
         */
        this.structureFilterService.setInnerHtml(
          structure,
          this.structureFilterService.renderFilterInnerHtml(
            displayState
          )
        );
        return true;
      },
      onReady: (ts, api) => {

        this.treeService = ts;

        this.coEventService.emit(CoEvent.StructureTreeReady, api);
        this.projectSettingsService.maxProjectLevel$
          .pipe(
            untilDestroyed(this),
            distinctUntilChanged()
          )
          .subscribe(level => {
            this.maxVisibleLevel = level;

            if (this.treeService && this._structures?.length) {
              this.treeService.setData([...this.structures] as any);
            }
          });


        this.treeService.selectedNode$
        .pipe(untilDestroyed(this))
        .subscribe(n => {
          if (n) {
            this.selected = n.data;
            this.treeService.showNode(n);
          }
        });

        this.appContainerService.isLeftPaneOpen
          .pipe(
            untilDestroyed(this),
            distinctUntilChanged(),
            filter(isOpen => isOpen),
          )
          .subscribe(() => {
            setTimeout(() => {
              this.treeService?.gridApi?.sizeColumnsToFit();
            }, 100);
          });

        this.resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect?.width;
          if (width > 0) {
            this.treeService?.gridApi?.sizeColumnsToFit();
          }
        });
        this.resizeObserver.observe(this.hostRef.nativeElement);

      },
      getNodeClass(node) {
        if (node.data.selectable === false) {
          return 'mydocma-mm-readonly-node';
        }
      },
    };
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.structures && this.treeService?.gridApi) {
      this.treeService.gridApi.sizeColumnsToFit();
    }
  }

  ngOnInit() {
    // const project = await firstValueFrom(this.projectService.currentProject);
    // const permissions = await firstValueFrom(this.permissionService.getProjectPermissions(project.Id));
    // const showIdColumns = permissions.AdminActions.find(x => x == ActionPermissionEnum.ShowIdColumns);
    // if (!showIdColumns) {
    //   this.structureFilterService.ADDRESS_FIELD_ORDER =
    //     this.structureFilterService.ADDRESS_FIELD_ORDER.filter(
    //       field => field !== 'Id' && field !== 'Uid'
    //     );
    // }
  }

  private applyStructurePatch(patch: StructureSyncPatch): void {
    if (!this.treeService) {
      return;
    }
    if (patch.op === 'update') {
      this.treeService.updateNode(patch.node);
    } else if (patch.op === 'delete') {
      this.treeService.removeNode(patch.node);
    } else if (patch.op === 'reorder' && patch.parent) {
      const parentId = patch.parent.Id?.toString();
      const newOrder = patch.parent.Children ?? [];
      if (parentId) {
        this.treeService.reorderChildren(parentId, newOrder);
      }
    } else if (patch.op === 'create' && patch.parent) {
      const parentId = patch.parent.Id?.toString();
      if (parentId) {
        try {
          this.treeService.addNode(patch.node, parentId);
        } catch (e) {
          this.logError('create addNode failed', e, { uid: patch.node?.MyDocmaUid, parentId });
        }
      }
    }
  }

  private logError(stage: string, error: unknown, info: Record<string, unknown>): void {
    const payload = { stage, error: String(error), ...info };
    console.error('Structure sync', stage, error, info);
    this.logger.error(`Structure sync ${stage} ${JSON.stringify(payload)}`, '', 'MmStructureTreeComponent');
  }


  onClick(node: TreeNode) {
    if (this.platformModeStateService.isLocalModeEnabled && this.connectivityService.isOfflineVal && !this.platformModeStateService.isLocalMode) {
      this.platformModeStateService.showNoInternetMessage$.next({show: true, cooldown: false, isLocalMode: false});
      return;
    }
    if (node) {
      this.structureClick.emit(node.data);
    }
  }

  protected async onAddressInfoClick(node: any, nodeElement: HTMLElement) {
    if (this.appContainerService.isMobileView) {
      const dialogRef = this.dialog.open(MmStructureAddressesDialogComponent, {
        data: {
          currentAddresses: this.structureAddressesService.StructureAddresses.get(node.data.Id),
          currentStructure: node.data,
          dialogMode: true
        },
        panelClass: ['mydocma-dialog-xs-fullscreen', 'mydocma-mm-structure-addresses-dialog'],
        autoFocus: false,
        disableClose: true
      });
    }
    else {
      if (!this.isAddressInfoOpen) {
        this.currentAddresses = this.structureAddressesService.StructureAddresses.get(node.data.Id);
        this.currentStructure = node.data;
        this.AddressInfoNodeElement = nodeElement;
        this.renderer2.addClass(nodeElement, 'address-info-open');

        // console.warn('this.currentAddresses', this.currentAddresses);
      }
      this.isAddressInfoOpen = !this.isAddressInfoOpen;
    }
  }

  protected onAddressMenuClosed() {
    this.renderer2.removeClass(this.AddressInfoNodeElement, 'address-info-open');
    this.AddressInfoNodeElement = undefined;
    this.isAddressInfoOpen = !this.isAddressInfoOpen;
    this.isExpanded = false;
    this.addressesComponent?.setIsExpanded(false);
  }





}
